import { useState, useEffect, useCallback } from 'react';
import SEO from '../../components/common/SEO';
import OptimizedImage from '../../components/common/OptimizedImage/OptimizedImage';
import { galleryService } from '../../services/galleryService';
import './Gallery.css';

const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [categories, setCategories] = useState([{ id: 'all', label: 'All' }]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load gallery images from Firebase
  useEffect(() => {
    loadGallery();
  }, [activeCategory]);

  const loadGallery = async () => {
    setLoading(true);
    setError(null);
    try {
      const images = await galleryService.getPublicGallery(activeCategory);
      setGalleryImages(images);

      // Load categories if viewing all
      if (activeCategory === 'all') {
        const cats = await galleryService.getCategories();
        const categoryOptions = [
          { id: 'all', label: 'All' },
          ...cats.map(cat => ({
            id: cat,
            label: cat.charAt(0).toUpperCase() + cat.slice(1)
          }))
        ];
        setCategories(categoryOptions);
      }
    } catch (err) {
      console.error('Error loading gallery:', err);
      setError('Failed to load gallery. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (image) => {
    setLightboxImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = useCallback(() => {
    setLightboxImage(null);
    document.body.style.overflow = '';
  }, []);

  // Restore body scroll on unmount and handle ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [closeLightbox]);

  const navigateImage = (direction) => {
    const currentIndex = galleryImages.findIndex(img => img.id === lightboxImage.id);
    let newIndex;

    if (direction === 'next') {
      newIndex = currentIndex === galleryImages.length - 1 ? 0 : currentIndex + 1;
    } else {
      newIndex = currentIndex === 0 ? galleryImages.length - 1 : currentIndex - 1;
    }

    setLightboxImage(galleryImages[newIndex]);
  };

  return (
    <main className="gallery-page">
      <SEO
        title="Gallery | KO Holistic Health Sydney — Healing Spaces & Events"
        description="Explore our gallery of healing sessions, sacred spaces, workshops and wellness events. See the peaceful environment where Reiki, meditation and holistic healing take place at KO Holistic Health Sydney."
        url="/gallery"
        keywords="holistic health gallery sydney, healing sessions photos, wellness events sydney, reiki healing space, meditation studio sydney, holistic wellness gallery, energy healing space"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Gallery', path: '/gallery' }]}
      />
      <section className="gallery-hero">
        <div className="container">
          <h1>Gallery</h1>
          <p>A glimpse into the healing journey and sacred spaces</p>
        </div>
      </section>

      <section className="gallery-content section">
        <div className="container">
          <div className="gallery-filters">
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`gallery-filter ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="gallery-loading">
              <div className="gallery-loading__spinner"></div>
              <p>Loading gallery...</p>
            </div>
          ) : error ? (
            <div className="gallery-error">
              <p>{error}</p>
              <button onClick={loadGallery} className="btn btn-primary">
                Try Again
              </button>
            </div>
          ) : galleryImages.length === 0 ? (
            <div className="gallery-empty">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <p>No images in this category yet</p>
            </div>
          ) : (
            <div className="gallery-grid">
              {galleryImages.map((image, index) => (
                <div
                  key={image.id}
                  className="gallery-item"
                  onClick={() => openLightbox(image)}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <OptimizedImage
                    src={image.url}
                    alt={image.alt || image.caption || 'Gallery image'}
                    aspectRatio="1/1"
                    objectFit="cover"
                  />
                  {(image.caption || image.alt) && (
                    <div className="gallery-item__overlay">
                      <span>{image.caption || image.alt}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {lightboxImage && (
        <div className="lightbox" onClick={closeLightbox}>
          <button className="lightbox__close" onClick={closeLightbox} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <button
            className="lightbox__nav lightbox__nav--prev"
            onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
            aria-label="Previous image"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <div className="lightbox__content" onClick={(e) => e.stopPropagation()}>
            <img src={lightboxImage.url} alt={lightboxImage.alt || lightboxImage.caption} />
            {(lightboxImage.caption || lightboxImage.alt) && (
              <p className="lightbox__caption">{lightboxImage.caption || lightboxImage.alt}</p>
            )}
          </div>

          <button
            className="lightbox__nav lightbox__nav--next"
            onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
            aria-label="Next image"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      )}
    </main>
  );
};

export default Gallery;
